import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '@platform/core/lib/cx';
import type { IApiErrorPlain, IAssignment } from '@platform/core/types';
import { useAddAssignmentMutation, useAssignmentsQuery, useMeQuery } from '@platform/state/api';
import { ErrorBanner, Spinner } from '@platform/ui';
import { useWorkspaceMembers } from '../fields/options/useWorkspaceMembers';
import type { IAssignmentPatch } from './api/IAssignmentPatch';
import { useRemoveAssignmentMutation, useUpdateAssignmentMutation } from './api/recordTabsApi';
import { AssignmentRow } from './assignments/AssignmentRow';
import { asPlainError, isUnsupported } from './files/asPlainError';
import type { IAssignmentsTabProps } from './IAssignmentsTabProps';
import { useRecordRefresh } from './useRecordRefresh';

/**
 * Вкладка «Доручення» (UIUX forms/tabs, прототип v4): «+ Додати доручення» (на поточного користувача; перше — відповідальний) ·
 * «Позначити все виконаним» · фільтри «Усі · Відкрито · Виконано» · поступ «N з M виконано»; сітка доручень зі зміною на місці («Хто» —
 * учасники в порядку прототипу: я першим, далі порядок членства).
 * Поки бекенд не підтримує зміну доручень, рядки лишаються для читання з поясненням.
 */
export function AssignmentsTab({ ws, record }: IAssignmentsTabProps) {
  const { t } = useTranslation();
  const { data = [], isLoading } = useAssignmentsQuery(record.id);
  const members = useWorkspaceMembers(ws);
  const { data: me } = useMeQuery();
  const [add, { error: addError }] = useAddAssignmentMutation();
  const [update] = useUpdateAssignmentMutation();
  const [remove] = useRemoveAssignmentMutation();
  const refresh = useRecordRefresh(record);
  const [filter, setFilter] = useState<'all' | 'open' | 'done'>('all');
  const [error, setError] = useState<IApiErrorPlain | null>(null);
  const [readOnly, setReadOnly] = useState(false);
  const canEdit = record.permissions?.edit !== false && !readOnly;
  const fail = (e: unknown) => { const p = asPlainError(e); if (isUnsupported(p)) { setReadOnly(true); setError({ ...p, message: t('recordPage.assignments.readOnlyHint') }); } else setError(p); };
  const change = (a: IAssignment, patch: IAssignmentPatch) => void update({ recordId: record.id, assignmentId: a.id, body: patch }).unwrap().then(() => setError(null)).catch(fail);
  const drop = (a: IAssignment) => void remove({ recordId: record.id, rt: record.recordType, assignmentId: a.id }).unwrap().catch(fail);
  const done = data.filter((a) => a.status === 'Done').length;
  const shown = data.filter((a) => filter === 'all' || (filter === 'done' ? a.status === 'Done' : a.status !== 'Done'));
  const addMine = () => { if (me) void add({ recordId: record.id, body: { userId: me.id, role: data.length ? 'Contributor' : 'Responsible' } }).unwrap().then(() => { setFilter('all'); refresh(); }).catch(() => undefined); };
  if (isLoading) return <Spinner />;
  return (
    <div className="rtab-assignments">
      <div className="rtab-asgbar" role="toolbar" aria-label={t('recordPage.assignments.filters')}>
        {canEdit && <button type="button" className="rtab-btn rtab-btn--primary rtab-btn--wide" onClick={addMine}>{t('recordPage.assignments.add')}</button>}
        {canEdit && <button type="button" className="rtab-btn rtab-btn--light" disabled={!data.length || done === data.length} onClick={() => data.filter((a) => a.status !== 'Done').forEach((a) => change(a, { status: 'Done' }))}>{t('recordPage.assignments.markAll')}</button>}
        {canEdit && <span className="rtab-sep" aria-hidden />}
        {(['all', 'open', 'done'] as const).map((k) => (
          <button key={k} type="button" className={cx('rtab-chipbtn', 'rtab-chipbtn--strong', filter === k && 'is-on')} aria-pressed={filter === k} onClick={() => setFilter(k)}>
            {t(`recordPage.assignments.${k}`, { count: k === 'all' ? data.length : k === 'done' ? done : data.length - done })}
          </button>
        ))}
        <span className="rtab-bar__spacer" />
        <div className="rtab-asgbar__progress">
          <span className="rtab-meter"><span style={{ width: `${data.length ? Math.round((done / data.length) * 100) : 0}%` }} /></span>
          <span className="rtab-bar__summary">{t('recordPage.assignments.progress', { done, total: data.length })}</span>
        </div>
      </div>
      <ErrorBanner error={addError ?? error} />
      <div className="rtab-box">
        <div className="rtab-asg rtab-asg--head" aria-hidden>
          <span /><span>{t('recordPage.assignments.who')}</span><span>{t('recordPage.assignments.role')}</span><span>{t('recordPage.assignments.what')}</span>
          <span>{t('recordPage.assignments.due')}</span><span>{t('recordPage.assignments.status')}</span><span />
        </div>
        {shown.map((a) => <AssignmentRow key={a.id} assignment={a} members={members} canEdit={canEdit} onChange={change} onRemove={drop} />)}
        {!shown.length && <p className="rtab-files__empty">{data.length ? t('recordPage.assignments.noMatch') : t('recordPage.assignments.empty')}</p>}
      </div>
    </div>
  );
}
